import { isNegatingUserOperator } from "../utils/workflow-condition-operators";

/**
 * Condition labels shared by the step card and the branch precondition badge.
 *
 * The card renders JSX and resolves ids against loaded collections; the badge
 * only needs plain text for a title attribute. The type -> label map is the part
 * they have in common, so it lives here rather than being written twice.
 */

export const conditionTypeLabels = (t) => ({
    amount_lower: t.read("rule.the_amount_is_lower"),
    amount_greater: t.read("rule.the_amount_is_greater"),
    supplier_number: t.read("rule.the_supplier_number"),
    cost_type: t.read("rule.the_cost_type_equals"),
    cost_type_not_equal: t.read("rule.the_cost_type_not_equals"),
    client_number: t.read("rule.the_client_number_equals"),
    gl_unit_number: t.read("rule.the_gl_unit_number_equals"),
    client_number_not_equal: t.read("rule.the_client_number_not_equals"),
    invoice_billable: t.read("rule.invoice_is_set_as"),
    item_billable: t.read("rule.item_billable"),
    invoice_custom: t.read("rule.invoice_is_custom"),
    supplier_name_equals: t.read("rule.supplier_name_equals"),
    gl_unit_is: t.read("rule.gl_unit_is"),
    gl_unit_is_not: t.read("rule.gl_unit_is_not"),
    user_has_role: t.read("rule.user_has_role"),
    user_has_function: t.read("rule.user_has_function"),
    line_item_amount: t.read("rule.the_line_item_amount"),
    item_description: t.read("rule.item_description"),
    item_cost_type: t.read("rule.item_cost_type"),
    item_gl: t.read("rule.item_gl"),
    tax_code: t.read("rule.invoice_tax_code"),
    item_tax_code: t.read("rule.item_tax_code"),
    currency: t.read("rule.currency_is"),
    category: t.read("rule.the_category_is"),
    client_category: t.read("rule.the_client_category_is"),
    invoice_number_missing: t.read("rule.invoice_number_is_missing"),
    purchase_and_payment: t.read("rule.purchase_and_payment"),
    invoice_model: t.read("rule.invoice_model"),
    invoice_type: t.read("rule.the_invoice_type_is"),
    item_gl_class: t.read("rule.item_gl_class"),
    item_gl_category: t.read("rule.item_gl_category"),
    user_is: t.read("rule.the_user"),
    user_in_department: t.read("rule.user_in_department"),
});

// Categories and departments store an ObjectId. The card resolves them against
// loaded collections; this badge has none, and a hexadecimal id reads worse than
// the type label alone.
const isOpaqueIdentifier = (value) => typeof value === "string" && /^[0-9a-f]{24}$/i.test(value.trim());

const describeValue = (value, t) => {
    if (value === null || value === undefined || value === "") return "";
    if (isOpaqueIdentifier(value)) return "";
    // GL ranges are stored as {min, max}.
    if (typeof value === "object" && !Array.isArray(value)) {
        const bounds = [value.min, value.max].filter((v) => v !== undefined && v !== "");
        if (bounds.length) return bounds.join(` ${t.read("rule.and")} `);
        return "";
    }
    if (Array.isArray(value)) {
        // User pickers store denormalised people; show who, not an object count.
        return value
            .map((entry) => {
                if (typeof entry !== "object" || entry === null) return String(entry);
                const name = [entry.first_name, entry.last_name].filter(Boolean).join(" ").trim();
                return name || entry.email || entry.username || entry.label || "";
            })
            .filter(Boolean)
            .join(", ");
    }
    return String(value);
};

/**
 * Types whose label does not already carry the comparison, so the operator has to
 * be spelled out. Shared with the step card: without it "the amount is lower"
 * reads as "the amount is lower equals 15000".
 */
export const TYPES_WITH_OPERATOR = [
    "item_description",
    "line_item_amount",
    "item_cost_type",
    "item_gl",
    "supplier_number",
    "tax_code",
    "item_tax_code",
];

// These read "is" / "is not" rather than "equals" / "is different", the way the
// editor offers them; otherwise a rule and its opposite read identically.
const TYPES_WITH_IS_WORDING = ["item_gl_class", "item_gl_category", "user_is"];

/** One condition as plain text, for a title attribute. */
export const summariseCondition = (condition, t) => {
    if (!condition) return "";
    let label = conditionTypeLabels(t)[condition.type] || condition.type;
    if (TYPES_WITH_OPERATOR.includes(condition.type) && condition.operator) {
        label += ` ${t.read(`rule.${condition.operator}`)}`;
    } else if (TYPES_WITH_IS_WORDING.includes(condition.type)) {
        // Same helper the card uses: it also catches not_equal / not_contain and a
        // stored list, so a rule cannot read "is not" there and "is" here.
        label += ` ${t.read(isNegatingUserOperator(condition.operator)
            ? "rule.operator_is_not"
            : "rule.operator_is")}`;
    }
    const value = describeValue(condition.value, t);
    return value ? `${label}: ${value}` : label;
};
